import PageHeader from "components/Page/PageHeader";
import { Box, Flex } from "@chakra-ui/layout";
import { useState, useCallback, useMemo } from "react";
import ListingPageHeaderBar from "ui/components/Page/ListingPageHeaderBar";
import useFiltroRelatorioComposicaoReceitasHorizontal from "../../hooks/useFiltroRelatorioComposicaoReceitasHorizontal";
import { useCurrentTenant } from "features/auth/tenant.store";
import ModalFiltrosRelatorioComposicaoReceitas from "ui/components/Relatorio/FiltroRelatorioComposicaoContas/ModalFiltrosRelatorioComposicaoContas";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import moment from "moment";
import { exportExcelRelatorioComposicaoReceitasHorizontal } from "../util/helper";
import PDFRelatorioComposicaoReceitasHorizontal from "../PDFRelatorio/index";
import { AssinaturasItem } from "components/Relatorio/FormAssinaturas";
import { trackEvent } from "infra/helpers/event";
import { RelatorioReceitaComposicaoHorizontalEvents } from "core/features/relatorios/enums/events";
import ExportMenu from "components/ExportMenu";
import SelectTipoData from "components/Input/SelectTipoData";
import DateRangePicker from "components/Input/DateRangePicker";
import { OperacaoEnum } from "core/resources/enums/conta";
import useRelatorioComposicaoReceitasHorizontal from "core/features/relatorios/hooks/useRelatorioComposicaoReceitasHorizontal";
import NewTable from "components/NewTable";
import useRelatorioComposicaoReceitasHorizontalTableColumns from "./../../hooks/useRelatorioComposicaoReceitasHorizontalTableColumns";
import useRelatorioComposicaoReceitasHorizontalPdf from "core/features/relatorios/hooks/useRelatorioComposicaoReceitasHorizontalPdf";
import ModalSelectFieldsRelatorio from "ui/components/ModalSelectFieldsRelatorio/index";

function ListagemReceitas() {
  const [exportType, setExportType] = useState<
    { type: "pdf" | "excel" } | undefined
  >();

  const { filtro, onChangeFiltro, onResetFiltro } =
    useFiltroRelatorioComposicaoReceitasHorizontal();

  const {
    data: dataComposicaoReceitasHorizontal,
    isLoading,
    isFetching,
  } = useRelatorioComposicaoReceitasHorizontal(filtro);

  const todasAsCategorias = useMemo(
    () => dataComposicaoReceitasHorizontal?.todasAsCategorias ?? [],
    [dataComposicaoReceitasHorizontal]
  );

  const columns =
    useRelatorioComposicaoReceitasHorizontalTableColumns(todasAsCategorias);

  const [isOpenModalFiltro, setIsOpenModalFiltro] = useState(false);
  const { tenant } = useCurrentTenant();

  const { isLoading: isLoadingPdf, mutate: handleRelatorioPdf } =
    useRelatorioComposicaoReceitasHorizontalPdf(filtro);

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: `Relatório de Receitas com Composição Horizontal - ${moment().format(
      "DD_MM_YYYY"
    )}`,
    Component: PDFRelatorioComposicaoReceitasHorizontal,
    loading: isLoadingPdf,
  });

  const _onExportPdf = useCallback(
    async (groupByFields?: string[], assinaturas?: AssinaturasItem[]) => {
      trackEvent(RelatorioReceitaComposicaoHorizontalEvents.ExportarPDF);
      handleRelatorioPdf({} as any, {
        onSuccess: (data) => {
          onExportPdf({
            data: { ...data, groupByFields, assinaturas },
          }).then(() => setExportType(undefined));
        },
      });
    },
    [handleRelatorioPdf, onExportPdf]
  );

  const _onExportExcel = useCallback(
    async (groupByFields?: string[]) => {
      trackEvent(RelatorioReceitaComposicaoHorizontalEvents.ExportarExcel);
      handleRelatorioPdf({} as any, {
        onSuccess: (data) => {
          exportExcelRelatorioComposicaoReceitasHorizontal({
            data,
            todasAsCategorias: data?.todasAsCategorias,
            groupByFields,
          });
        },
      });
    },
    [handleRelatorioPdf]
  );

  const numeroFiltroSelecionados =
    (filtro.contasCategoriasIds?.length ? 1 : 0) +
    (filtro.dataInicio || filtro.dataFim ? 1 : 0) +
    (filtro.tipoData ? 1 : 0) +
    (filtro.contasBancariasIds?.length ? 1 : 0) +
    (filtro.centrosDeCustoIds?.length ? 1 : 0);

  return (
    <>
      <PageHeader
        justifyContent="space-between"
        title="Relatório de receitas com composição horizontal"
        midComponent={
          <Flex gap={4}>
            <Box>
              <SelectTipoData
                bg="white"
                style={{ border: "1px solid #ccc", borderRadius: 6 }}
                value={filtro.tipoData}
                onChange={(v) => onChangeFiltro({ tipoData: v })}
                operacao={OperacaoEnum.Receita}
              />
            </Box>

            <Box>
              <DateRangePicker
                onChange={(dataInicio, dataFim) => {
                  onChangeFiltro({
                    dataInicio,
                    dataFim,
                  });
                }}
                value={{
                  dataInicio: filtro.dataInicio,
                  dataFim: filtro.dataFim,
                }}
              />
            </Box>
          </Flex>
        }
      />

      <ListingPageHeaderBar
        valueSearch={filtro.search}
        onSearch={(search) => onChangeFiltro({ search })}
        visibleFiltro
        badge
        badgeValue={numeroFiltroSelecionados}
        onFiltro={() => setIsOpenModalFiltro(true)}
        children={
          dataComposicaoReceitasHorizontal?.items?.length ? (
            <Flex justifyContent="flex-end" w="100%">
              <ExportMenu
                onExportPdf={() => setExportType({ type: "pdf" })}
                onExportarXlsx={() => setExportType({ type: "excel" })}
              />
            </Flex>
          ) : null
        }
      />

      <Box mt={4}>
        <NewTable
          loading={isLoading || isFetching}
          columns={columns}
          dataSource={dataComposicaoReceitasHorizontal?.items}
          customPagination={{
            currentPage:
              dataComposicaoReceitasHorizontal?.meta?.currentPage ?? 1,
            totalItems:
              dataComposicaoReceitasHorizontal?.meta?.totalItems ??
              dataComposicaoReceitasHorizontal?.items?.length,
            itemsPerPage:
              dataComposicaoReceitasHorizontal?.meta?.itemsPerPage ??
              dataComposicaoReceitasHorizontal?.items?.length,
            onChangePage: (page, limit) => onChangeFiltro({ page, limit }),
          }}
        />
      </Box>

      <ModalFiltrosRelatorioComposicaoReceitas
        tenantId={tenant.id}
        filtro={filtro}
        onChangeFiltro={onChangeFiltro}
        onReset={onResetFiltro}
        visible={isOpenModalFiltro}
        onClose={() => setIsOpenModalFiltro(false)}
        operacao={OperacaoEnum.Receita}
      />

      <ModalSelectFieldsRelatorio
        visible={!!exportType?.type}
        onClose={() => setExportType(undefined)}
        includeAssinaturas={exportType?.type === "pdf"}
        onSubmit={({ assinaturas, groupByFields }) => {
          exportType?.type === "pdf"
            ? _onExportPdf(
                groupByFields?.sort((a, b) => a.localeCompare(b)),
                assinaturas
              )
            : _onExportExcel(groupByFields?.sort((a, b) => a.localeCompare(b)));
        }}
        fields={[]}
        groupByFields={[
          {
            label: "Unidade",
            value: "unidade",
          },
          {
            label: "Data de Crédito",
            value: "dataMovimentacao",
          },
        ]}
      />
    </>
  );
}

export default ListagemReceitas;
